<script>
import { defineAsyncComponent } from 'vue';
import "@common/syncfusionCss";
import { sDs } from "@common/sDs/sDs";
import { componentStore } from "@common/componentStore/componentStore";
import { controlStore, createStoreIndex } from "@common/control/controlStore";
import { library } from "@common/library/library";
import { uStore } from "@common/userStore";

const editorSettings = {
    tabListComponentId: {
        type: 'dropdown',
        id: 'tabListComponentId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: '"tabList" === this.item.cType',
        fields: { text: 'cId', value: 'cId' },
        label: 'Tab List Component',
        helpText: 'ID of tab list component that should be used.',
        default: 'tabList_importTool'
    },
    messageEmptyImportsComponentId: {
        type: 'dropdown',
        id: 'messageEmptyImportsComponentId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: '"messagePanel" === this.item.cType',
        fields: { text: 'cId', value: 'cId' },
        label: 'Message Panel Component when no Imports found',
        helpText: 'ID of message panel component that should be used.',
        default: 'messagePanel_import_no_imports'
    },
    gridFieldMappingComponentId: {
        type: 'dropdown',
        id: 'gridFieldMappingComponentId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: '"grid" === this.item.cType',
        fields: { text: 'cId', value: 'cId' },
        label: 'Grid Component for fields mapping',
        helpText: 'ID of grid component that should be used.',
        default: 'grid_import_field_mapping'
    },
    gridPastImportsComponentId: {
        type: 'dropdown',
        id: 'gridPastImportsComponentId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: '"grid" === this.item.cType',
        fields: { text: 'cId', value: 'cId' },
        label: 'Grid Component for past imports',
        helpText: 'ID of grid component that should be used.',
        default: 'grid_import_past_imports'
    },
    gridImportDetailsComponentId: {
        type: 'dropdown',
        id: 'gridImportDetailsComponentId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: '"grid" === this.item.cType',
        fields: { text: 'cId', value: 'cId' },
        label: 'Grid Component for import details',
        helpText: 'ID of grid component that should be used.',
        default: 'grid_import_import_details'
    }
};

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

const gridComponent = defineAsyncComponent(() => import('@common/grid/grid.vue'));

let importConfig = {
    cId: "import",
    cType: "import",
    cTitle: "Import",
    cTemplate: `<div v-if="!loadingLocal" id="import" class="apply-bootstrap">
                    <div v-show="!importStarted" id="pastImports">
                        <div class="title">Import Tool</div>
                        <!-- <div class="sub-title">Past Imports</div> -->
                        <div v-if="!pastImports.length">
                            <message-panel :c-id="messageEmptyImportsComponentId"></message-panel>
                        </div>
                        <div v-else id="gridPastImports">
                            <grid :c-id="gridPastImportsComponentId" :prop-data="pastImports" :prop-load-func="gridPastImportsLoad"></grid>
                            <grid v-show="false" :c-id="gridImportDetailsComponentId" :ref="gridImportDetailsComponentId"></grid>
                        </div>
                        <control name="IMPORT_START"></control>
                    </div>
                    <div v-show="importStarted" id="importWizard">
                        <tab-list
                            :c-id="tabListComponentId"
                            :prop-validation-func="validationStep"
                            :prop-cancel-func="close"
                            :prop-load-func="tabListLoad"
                            :prop-tab-change-func="tabChange"
                            :prop-finish-func="finish">
                        </tab-list>
                    </div>
                </div>
                `,
    controls: [
        {
            name: 'IMPORT_START',
            type: "button",
            label: "Start new import",
            ref: 'IMPORT_START',
            onclick: 'setImport',
            cssClass: 'e-success',
            width: '200px'
        },
    ]
};

const importCmp = {
    name: 'import',
    editorSettings: editorSettings,
    preInitControls: true,
    data() {
        return {
            tabListComponent: false,
            gridPastImports: false,
            timeouts: {},
            intervals: {},
            importID: 116,
            fileName: false,
            fieldsList: [],
            resultsList: [],
            importCompleted: false,
            pastImports: [],
            loadingLocal: true,
            importStarted: false,
            pspID: 1,
            checkStatusTimeout: 5, // seconds,
            importCanceled: true
        };
    },
    computed: {
        tabListComponentId() {
            return this.config.tabListComponentId;
        },
        messageEmptyImportsComponentId() {
            return this.config.messageEmptyImportsComponentId;
        },
        gridFieldMappingComponentId() {
            return this.config.gridFieldMappingComponentId;
        },
        gridPastImportsComponentId() {
            return this.config.gridPastImportsComponentId;
        },
        gridImportDetailsComponentId() {
            return this.config.gridImportDetailsComponentId;
        }
    },
    components: {
        'message-panel': defineAsyncComponent(() => import('@common/messagePanel.vue')),
        grid: defineAsyncComponent(() => import('@common/grid/grid.vue')),
        tabList: defineAsyncComponent(() => import('@common/tabList/tabList.vue')),
        control: controlComponent
    },
    mounted() {
        if (window && window.opener && typeof window.opener.tryInactivityLogoutReset === 'function') {
            document.body.onclick = () => {
                if (window && window.opener && typeof window.opener.tryInactivityLogoutReset === 'function') window.opener.tryInactivityLogoutReset(true);
            };
        }
    },
    methods: {
        finish() {
            if (this.tabListComponent.controls['IMPORT_VALIDATE_ONLY'].value) {
                this.clearControls(2);
                this.tabListComponent.back(1);
                return;
            }
            this.tabListComponent.clearWizard();
            this.clearControls();
            this.importStarted = false;
            this.onLoad();
        },
        async onLoad() {
            await uStore().getUserData();
            this.sDs.post('sds_importList').then((response) => {
                this.pastImports = [];
                let pastImports = [];
                if (response.status === 'success') {
                    for (let i in response.data) {
                        if (i === 'length') continue;
                        pastImports.push({
                            importID: response.data[i]['IMPORT_ID'],
                            name: response.data[i]['IMPORT_FILE_NAME'],
                            totalRecords: response.data[i]['TOTAL_COUNT'],
                            newRecords: response.data[i]['ADDED_COUNT'],
                            updatedRecords: response.data[i]['UPDATED_COUNT'],
                            errorCount: response.data[i]['ERROR_COUNT'],
                            user: {
                                name: response.data[i]['CLIENT_NAME'],
                                email: response.data[i]['CLIENT_EMAIL'],
                            },
                            started: response.data[i]['IMPORT_START_TIME'],
                            finished: response.data[i]['IMPORT_FINISH_TIME']
                        });
                    }
                    this.pastImports = library.cloneObj(pastImports);
                } else {
                    this.showOnError(response);
                }
                this.loadingLocal = false;
            });
        },
        setImport() {
            this.importStarted = true;
        },
        setPspId() {
            this.pspID = this.tabListComponent.controls['IMPORT_FILE_TYPE'].value;
        },
        gridPastImportsLoad(gridPastImports) {
            if (!this.gridPastImports) this.gridPastImports = gridPastImports;
            if (gridPastImports.controls) {
                let showImportDetailsCtrl = library.cloneObj(gridPastImports.controls['SHOW_IMPORT_DETAILS']);
                showImportDetailsCtrl.onclick = this.showImportDetails;
                let controlStoreIndex = createStoreIndex(gridPastImports.storeId, showImportDetailsCtrl.name);
                controlStore().addControl(showImportDetailsCtrl, gridPastImports, controlStoreIndex);
            }
        },
        showImportDetails(event, control) {
            if (control && control.importID) {
                library.showLoadingIndicator('gridPastImports');
                this.sDs.post('sds_importList', control.importID, control.importStatus).then((response) => {
                    library.hideLoadingIndicator('gridPastImports');
                    if (response.status === 'success') {
                        let gridData = [];
                        for (let i in response.data) {
                            if (i === 'length') continue;
                            gridData.push({
                                rowNum: response.data[i]['IMPORT_DETAIL_ROW_NUM'],
                                recordID: response.data[i]['IMPORT_DETAIL_RECORD_ID'],
                                status: response.data[i]['IMPORT_DETAIL_STATUS'],
                                errors: response.data[i]['IMPORT_DETAIL_ERROR'],
                            });
                        }
                        let gridcId = this.gridImportDetailsComponentId;
                        let currentImport = this.pastImports.find((element) => element.importID === control.importID);
                        let fileName = (currentImport ? currentImport.name.split('.').slice(0, -1).join('.') : '') + '_report';
                        if (control.importStatus === -1) fileName += '_errors';
                        if (control.importStatus === 1) fileName += '_added';
                        if (control.importStatus === 2) fileName += '_updated';
                        if (fileName && componentStore
                            && componentStore.state
                            && componentStore.state.components
                            && componentStore.state.components[this.gridImportDetailsComponentId]) {
                            componentStore.state.components[this.gridImportDetailsComponentId].gridSettings.exportFileName = fileName;
                        }
                        let dialogContent = {
                            name: "import-details-popup",
                            template: '<div><grid :cId="gridcId" :prop-data="gridData"></grid></div>',
                            components: {
                                grid: gridComponent
                            },
                            data() {
                                return {
                                    gridData: gridData,
                                    gridcId: gridcId
                                };
                            }
                        };
                        library.showDialog(dialogContent, false, false, {
                            title: 'Import Details', name: 'importDetails', cssClass: 'importDetails', showAsAlert: true/* , resizeDialogToIframeHeight: 'grid_import_import_details_wrapper' */
                        });
                    } else {
                        this.showOnError(response);
                    }
                });
            }
        },
        async tabListLoad(tabListComponent) {
            if (!this.tabListComponent) {
                this.tabListComponent = tabListComponent;
                if (this.tabListComponent.controls) {
                    let tmpControls = library.cloneObj(this.tabListComponent.controls);
                    tmpControls['IMPORT_SAMPLE'].onclick = this.downloadSample;
                    tmpControls['IMPORT_GUIDE'].onclick = function () {
                        window.open('https://help.samaritan.com/import-tool', '_blank').focus();
                    };
                    tmpControls['IMPORT_FAQ'].onclick = function () {
                        window.open('https://help.samaritan.com/import-tool-faqs', '_blank').focus();
                    };
                    tmpControls['IMPORT_CANCEL'].onclick = this.importCancel;
                    tmpControls['IMPORT_FILE_TYPE'].onchange = this.setPspId;
                    let availablePspListResponse = await this.sDs.post('sds_importGetAvailablePspList');
                    let availablePspList = [];
                    if (availablePspListResponse && availablePspListResponse.status === 'success') {
                        availablePspList = library.cloneObj(availablePspListResponse.data);
                    }
                    tmpControls['IMPORT_FILE_TYPE'].items = library.cloneObj(availablePspList);
                    for (let i in tmpControls) {
                        let controlStoreIndex = createStoreIndex(tabListComponent.storeId, tmpControls[i].name);
                        controlStore().addControl(tmpControls[i], tabListComponent, controlStoreIndex);
                    }
                    this.checkFile();
                }
            }
        },
        tabChange(fromIndex, toIndex) {
            if (fromIndex >= 0 && toIndex >= 0) {
                if (fromIndex === 0 && toIndex === 1) {
                    this.tabListComponent.controls['IMPORT_FIELDS_LIST'].items = library.cloneObj(this.fieldsList);
                    this.tabListComponent.controls['IMPORT_FILE_NAME'].value = "The " + this.file.name + " (" + this.file.size + ") file uploaded successfully.";
                    this.tabListComponent.controls['IMPORT_FILE_RECORDS'].value = this.file.lineCount + " row(s) in file.";
                    this.clearControls(0);
                } else if (toIndex === 0) {
                    this.clearControls();
                    this.checkFile();
                } else if (toIndex === 2) {
                    this.importCanceled = false;
                    this.doImport();
                }
            }
        },
        async startImport(isValid, validateOnly) {
            let uniqueKeyColumns = [];
            let currentStore = store;
            if (this.layoutStoreId) currentStore = currentStore[this.layoutStoreId];
            this.previousSelectedUID = [];
            for (let i in this.fieldsList) {
                if (i === 'length') continue;
                if (!this.fieldsList[i].uniqueIdentifier) continue;
                if (currentStore.state[this.gridFieldMappingComponentId]
                    && currentStore.state[this.gridFieldMappingComponentId].controls
                    && currentStore.state[this.gridFieldMappingComponentId].controls['GRID_FIELD__' + i]
                    && currentStore.state[this.gridFieldMappingComponentId].controls['GRID_FIELD__' + i].value
                ) {
                    uniqueKeyColumns.push(this.fieldsList[i].field);
                }
            }
            this.previousSelectedUID = library.cloneObj(uniqueKeyColumns);
            if (!uniqueKeyColumns.length && this.tabListComponent.controls['IMPORT_UPLOAD_BEHAVIOUR'].value !== 1) {
                isValid = false;
                library.showDialog('Please select at least one Unique Identifier', false, false, { showAsAlert: true });
            }

            if (isValid) {
                let response = await this.sDs.post('sds_importStart', uniqueKeyColumns, this.tabListComponent.controls['IMPORT_UPLOAD_BEHAVIOUR'].value, this.tabListComponent.controls['IMPORT_UPDATE_BEHAVIOUR'].value, validateOnly, this.tabListComponent.controls['IMPORT_CREATE_GRID'].value);
                if (response.status === 'success') {
                    this.importID = response.data;
                } else {
                    let error = Array.isArray(response.error) ? response.error.join('<br/>') : response.error;
                    this.showOnError(response, true);
                    isValid = false;
                }
            }
            return isValid;
        },
        async validationStep(fromIndex, toIndex) {
            let isValid = true;
            if (fromIndex >= 0 && toIndex >= 0) {
                if (fromIndex === 0 && toIndex === 1) { // from upload to map
                    if (this.tabListComponent.controls && this.tabListComponent.controls['IMPORT_UPLOAD']) {
                        let uploadControl = this.tabListComponent.controls['IMPORT_UPLOAD'];
                        if (uploadControl.value.length) {
                            let fileName = uploadControl.value[0].name;
                            let fileContent = uploadControl.value[0].base64;
                            library.showLoadingIndicator('importExport');
                            let response = await this.sDs.post('sds_importUploadFile', this.pspID, fileName, fileContent);
                            library.hideLoadingIndicator('importExport');
                            if (response.status === 'success') {
                                this.fieldsList = [];
                                for (let i in response.data.map) {
                                    if (i === 'length') continue;
                                    this.fieldsList.push(
                                        {
                                            columnID: i,
                                            columnNumber: response.data.map[i].excelCol,
                                            columnTitle: response.data.map[i].colName,
                                            field: response.data.map[i].field,
                                            matched: response.data.map[i].field ? response.data.map[i].field : false,
                                            uniqueIdentifier: response.data.map[i].isPossibleKey
                                        }
                                    );
                                }
                                this.file = {
                                    name: fileName,
                                    size: response.data.fileSize,
                                    lineCount: response.data.linecount,
                                    pspName: response.data.pspName
                                };
                            } else if (response.error) {
                                if (response.error.indexOf('no data found')) {
                                    this.showOnError(response, true);
                                    this.tabListComponent.refs['IMPORT_UPLOAD'].clearAll();
                                    this.tabListComponent.controls['IMPORT_UPLOAD'].value = [];
                                    this.checkFile();
                                } else {
                                    this.showOnError(response);
                                }
                                isValid = false;
                            }
                        } else {
                            library.showDialog('Please select file', false, false, { showAsAlert: true });
                            isValid = false;
                        }
                    } else {
                        library.showDialog('Configuration error: IMPORT_UPLOAD control not found.', false, false, { showAsAlert: true });
                        isValid = false;
                    }
                }
                if (fromIndex === 1 && toIndex === 2) { // from map to progress
                    let validateOnly = this.tabListComponent.controls['IMPORT_VALIDATE_ONLY'].value;
                    isValid = await this.startImport(isValid, validateOnly);
                }
                if (fromIndex === 2 && toIndex === 1 && this.importID) { // from progress to map
                    isValid = false;
                    if (this.importCompleted) {
                        this.clearControls(2);
                        this.tabListComponent.back();
                    } else {
                        library.showDialog('Do you want to cancel current import and back to the previous step?',
                            () => {
                                this.sDs.post('sds_importCancel', this.importID).then((response) => {
                                    if (response.status === 'success') {
                                        this.clearControls(2);
                                        this.tabListComponent.back();
                                    } else {
                                        this.showOnError(response);
                                    }
                                });
                            },
                            false,
                            {
                                okButtonLabel: 'Back to the previous step',
                                cancelButtonLabel: 'Cancel'
                            });
                    }
                }
                if (fromIndex === 2 && toIndex === 3) {
                    if (!this.importCompleted) { // confirmation
                        isValid = false;
                    } else {
                        this.resultsList = [];
                        library.showLoadingIndicator('importExport');
                        let response = await this.sDs.post('sds_importList', this.importID);
                        library.hideLoadingIndicator('importExport');
                        if (response.status === 'success') {
                            let gridData = [];
                            for (let i in response.data) {
                                if (i === 'length') continue;
                                gridData.push({
                                    rowNum: response.data[i]['IMPORT_DETAIL_ROW_NUM'],
                                    recordID: response.data[i]['IMPORT_DETAIL_RECORD_ID'] < 0 ? 'Not found' : response.data[i]['IMPORT_DETAIL_RECORD_ID'],
                                    status: response.data[i]['IMPORT_DETAIL_STATUS'],
                                    errors: response.data[i]['IMPORT_DETAIL_ERROR'],
                                });
                            }
                            this.tabListComponent.controls['IMPORT_RESULTS_LIST'].items = gridData;
                        } else {
                            isValid = false;
                            this.showOnError(response, true);
                        }
                    }
                }
            }
            return isValid;
        },
        closeWindow() {
            window.close();
        },
        close() {
            if (this.importID) {
                library.showDialog('Are you sure you want to cancel current import and close the Import tool?', () => {
                    this.sDs.post('sds_importCancel', this.importID).then((response) => {
                        if (response.status === 'success') {
                            this.closeWindow();
                        } else {
                            this.showOnError(response);
                        }
                    });
                });
            } else {
                library.showDialog('Are you sure you want to close the Import tool?', () => {
                    this.closeWindow();
                });
            }
        },
        checkFile() {
            if (this.timeouts && typeof this.timeouts["checkFile"] !== "undefined") {
                clearTimeout(this.timeouts["checkFile"]);
            }
            if (this.tabListComponent
                && this.tabListComponent.controls
                && this.tabListComponent.controls['IMPORT_UPLOAD']
                && this.tabListComponent.controls['IMPORT_UPLOAD'].value.length) {
                this.tabListComponent.next();
                return;
            }
            if (this && this.timeouts) {
                this.timeouts["checkFile"] = setTimeout(() => {
                    if (this && this.checkFile) this.checkFile();
                }, 500); // NOSONAR
            }
        },
        redirectToResult() {
            if (this.timeouts && typeof this.timeouts["redirectToResult"] !== "undefined") {
                clearTimeout(this.timeouts["redirectToResult"]);
            }
            if (this.importCompleted) {
                this.tabListComponent.next();
                return;
            }

            this.timeouts["redirectToResult"] = setTimeout(() => {
                if (this && this.redirectToResult) this.redirectToResult();
            }, 2000); // NOSONAR
        },
        isCompleted(response) {
            if (response.status === 'success') {
                if (this.tabListComponent
                    && this.tabListComponent.controls
                    && this.tabListComponent.controls['IMPORT_PROGRESS']
                ) {
                    let persentage = parseInt(((response.data.progress || 1) / response.data.total) * 100, 10);
                    if (response.data.progress === 0) persentage = 0;
                    if (response.data.completed) persentage = 100;
                    this.tabListComponent.controls['IMPORT_PROGRESS'].value = persentage;
                }
                if (this.tabListComponent
                    && this.tabListComponent.controls
                    && this.tabListComponent.controls['IMPORT_LOG']
                ) {
                    this.tabListComponent.controls['IMPORT_LOG'].value = response.data.report;
                }
                if (response.data.completed) {
                    if (this.timeouts && typeof this.timeouts["checkProgress"] !== "undefined") {
                        clearTimeout(this.timeouts["checkProgress"]);
                    }
                    if (this.intervals && typeof this.intervals["inactivityLogout"] !== "undefined") {
                        clearInterval(this.intervals["inactivityLogout"]);
                    }
                    this.importCompleted = true;
                    this.tabListComponent.controls['IMPORT_CANCEL'].display = false;
                    this.tabListComponent.controls['IMPORT_COMPLETED'].display = true;
                    if (this.tabListComponent.controls['IMPORT_VALIDATE_ONLY'].value) {
                        this.redirectToResult();
                    }
                    return true;
                }
            }
            return false;
        },
        async doImport() {
            if (!this.importID || this.importCompleted || this.importCanceled) return;
            if (window
                && window.opener
                && typeof window.opener.tryInactivityLogoutReset === 'function'
                && this.intervals
                && typeof this.intervals["inactivityLogout"] === "undefined")
            {
                this.intervals['inactivityLogout'] = setInterval(() => {
                    if (window && window.opener && typeof window.opener.tryInactivityLogoutReset === 'function') window.opener.tryInactivityLogoutReset(true);
                }, 1000);
            }
            this.sDs.post('sds_importDoPart', this.importID, { sDsParams: { continueOnFail: false, force: true } }).then((response) => {
                if (!this.isCompleted(response)) {
                    this.doImport();
                }
            });
            this.checkProgress(true);
        },
        async checkProgress(onlySetTimeout) {
            if (this.timeouts && typeof this.timeouts["checkProgress"] !== "undefined") {
                clearTimeout(this.timeouts["checkProgress"]);
            }
            if (!this.importID || this.importCompleted || this.importCanceled) return;
            if (!onlySetTimeout) {
                let response = await this.sDs.post('sds_importCheckStatus', this.importID);
                if (this.isCompleted(response)) {
                    return;
                }
            }
            this.timeouts["checkProgress"] = setTimeout(() => {
                if (this && this.checkProgress) this.checkProgress();
            }, this.checkStatusTimeout * 1000); // NOSONAR
        },
        downloadSample() {
            this.sDs.post('sds_importDownloadSample', this.pspID, true).then((response) => {
                if (response.status === 'success') {
                    library.downloadURI(response.data);
                } else {
                    this.showOnError(response);
                }
            });
        },
        clearControls(step) {
            switch (step) {
                case 0:// upload tab
                    this.tabListComponent.refs['IMPORT_UPLOAD'].clearAll();
                    this.tabListComponent.controls['IMPORT_UPLOAD'].value = [];
                    break;
                case 1:// map
                    this.tabListComponent.controls['IMPORT_FILE_NAME'].value = '';
                    this.tabListComponent.controls['IMPORT_FILE_RECORDS'].value = '';
                    this.tabListComponent.controls['IMPORT_FIELDS_LIST'].items = [];
                    this.file = {};
                    this.importID = false;
                    this.importCompleted = false;
                    break;
                case 2:// progress
                    this.importID = false;
                    this.importCompleted = false;
                    this.tabListComponent.controls['IMPORT_LOG'].value = '';
                    this.tabListComponent.controls['IMPORT_PROGRESS'].value = 0;
                    this.tabListComponent.controls['IMPORT_COMPLETED'].display = false;
                    this.tabListComponent.controls['IMPORT_RESULTS_LIST'].items = [];
                    this.tabListComponent.controls['IMPORT_CANCEL'].display = true;
                    this.tabListComponent.controls['IMPORT_COMPLETED'].display = false;
                    if (this.previousSelectedUID && this.previousSelectedUID.length) {
                        for (let i in this.fieldsList) {
                            if (i === 'length') continue;
                            if (this.previousSelectedUID.indexOf(this.fieldsList[i].field) !== -1) {
                                this.fieldsList[i].checked = true;
                            } else {
                                this.fieldsList[i].checked = false;
                            }
                        }
                        this.tabListComponent.controls['IMPORT_FIELDS_LIST'].items = library.cloneObj(this.fieldsList);
                    }
                    break;
                default:
                    this.clearControls(0);
                    this.clearControls(1);
                    this.clearControls(2);
                    this.previousSelectedUID = [];
                    this.fieldsList = [];
                    this.tabListComponent.controls['IMPORT_UPLOAD_BEHAVIOUR'].value = this.tabListComponent.controls['IMPORT_UPLOAD_BEHAVIOUR'].default;
                    this.tabListComponent.controls['IMPORT_UPDATE_BEHAVIOUR'].value = this.tabListComponent.controls['IMPORT_UPDATE_BEHAVIOUR'].default;
                    this.tabListComponent.controls['IMPORT_VALIDATE_ONLY'].value = this.tabListComponent.controls['IMPORT_VALIDATE_ONLY'].default;
                    this.tabListComponent.controls['IMPORT_CREATE_GRID'].value = this.tabListComponent.controls['IMPORT_CREATE_GRID'].default;
                    this.checkFile();
                    break;
            }
        },
        importCancel() {
            if (!this.importID) return;
            library.showDialog('Are you sure you want to cancel current import?', () => {
                this.importCanceled = true;
                this.sDs.post('sds_importCancel', this.importID).then((response) => {
                    if (response.status === 'success') {
                        library.showDialog('Do you want to start again or close Import tool?',
                            () => {
                                this.clearControls();
                                this.tabListComponent.clearWizard();
                            },
                            () => {
                                this.close();
                            }, {
                                okButtonLabel: 'Start again',
                                cancelButtonLabel: 'Close'
                            });
                    } else {
                        this.showOnError(response);
                    }
                });
            });
        },
        showOnError(response, isAlert) {
            let error = Array.isArray(response.error) ? response.error.join('<br/>') : response.error;
            if (isAlert) {
                library.showDialog(error, false, false, { showAsAlert: true });
            } else {
                library.showToast(error);
            }
        }
    },
    unmounted() {
        for (let i in this.timeouts) {
            if (i === 'length') continue;
            clearTimeout(this.timeouts[i]);
        }
        for (let i in this.intervals) {
            if (i === 'length') continue;
            clearInterval(this.intervals[i]);
        }
    }
};

componentStore.initComponent(importCmp, importConfig);
export { importConfig, importCmp };
export default importCmp;
</script>

<style lang="css">
.container-fluid, .container-sm, .container-md, .container-lg, .container-xl {
    padding: 0;
}

#importExport {
    font-family: 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
    margin: 0;
}

.importDetails.e-dlg-modal {
    width: 90% !important;
}

/*.importDetails td {
    white-space: normal !important;
}*/

</style>

<style scoped>
h1, .h1 {
    font-size: 32px;
    font-weight: 400;
}

label {
    margin-bottom: 0;
}

button, input, select, textarea {
    background-color: transparent;
    border-style: none;
}

:deep(#importUploadTab), :deep(#importMapTab), :deep(#importConfirmationTab) {
    width: 60%;
    margin: 0 auto;
    min-width: 500px;
}

:deep(#importProgressTab) {
    width: 80%;
    margin: 0 auto;
    min-width: 500px;
}

#gridPastImports {
    margin-bottom: 20px;
}

#pastImports {
    width: 90%;
    margin: 0 auto;
    min-width: 900px;
}

:deep(.no-found-count) {
    /*text-align: center;*/
}

:deep([name^="SHOW_IMPORT_DETAILS"]) {
    text-align: left;
    padding: 0;
}

:deep(#importUploadTab .help-links-controls) {
    display: flex;
    max-width: 500px;
    margin: 0 auto;
}

::deep(#importConfirmationTab .title)m #import .title  {
    text-align: center;
    font-size: 24px;
    margin-bottom: 10px;
    margin-top: 30px;
    color: #63727f;
}

:deep(#importUploadTab .title) {
    text-align: left;
    font-size: 24px;
    margin-bottom: 10px;
    margin-top: 30px;
    color: #63727f;
}

:deep(#pastImports .title) {
    text-align: center;
    font-size: 36px;
    margin-bottom: 15px;
    margin-top: 15px;
    color: #FC785E;
}

:deep(#pastImports .sub-title) {
    background-color: #e3e4e8;
    color: #000000;
    font-size: 14px;
    width: 110px;
    text-align: center;
    padding: 10px;
}

:deep(#importUploadTab .title) {
    margin-bottom: 10px;
}

:deep(#importUploadTab .help-links-title) {
    font-size: 14px;
    margin-top: 40px;
    text-align: center;
    font-weight: bold;
}

:deep(#importUploadTab .importFileTypeRadio) {
    align: center;
    width: 100%;
    margin: 0 auto;
}

:deep(#importUploadTab .line-break) {
    text-align: center;
    line-height: 29px;
}

:deep(#importUploadTab .help-links) {
    width: 70%;
    margin: 0 auto;
}

:deep(#importUploadTab .uploader a) {
    color: #2798b1;
    font-weight: bold;
}

:deep(#importUploadTab .help-links-controls > div) {
    flex-grow: 10;
}

:deep(#importUploadTab .help-links-controls span) {
    text-transform: initial;
    color: #2798b1;
}

:deep(.clearfix) {
    clear: both;
}

:deep(.e-icons.found){
    color: green;
    font-size: 15px;
    font-weight: 900;
}

:deep(.e-icons.found:before){
    content: '\e7ff' !important;
}
:deep(.e-icons.not-found){
    color: red;
    font-size: 20px;
    font-weight: 900;
}

:deep(#importProgressTab .progressIndicator) {
    width: 20%;
    min-height: 300px;
    float: left;
}

:deep(#importProgressTab .log) {
    width: 80%;
    min-height: 300px;
    float: left;
}

:deep(#importProgressTab) {
    min-height: 300px;
}

:deep(#importProgressTab .log .label-wrapper) {
    margin: 0;
}
:deep(#importMapTab .importValidateOnly) {
    display: flex;
    width: 700px;
    margin: 0 auto;
    font-size: 16px;
}

:deep(#importMapTab .importCreateGrid) {
    display: flex;
    width: 700px;
    margin: 0 auto;
    font-size: 16px;
}

:deep(#importMapTab .title) {
    text-align: center;
    font-size: 14px;
    margin-top: 20px;
    margin-bottom: 10px;
    color: black;
}

:deep(#importMapTab .title .e-control) {
    display: inline;
    font-size: 14px !important;
    color: black;
}

:deep(#importMapTab .label-text) {
    color: #757575;
}

:deep(#importMapTab .checkbox-list-input) {
    margin: 0;
}

:deep(#importMapTab .mapping-controls) {
    display: flex;
    width: 700px;
    margin: 0 auto;
}

:deep(#importMapTab .add-controls) {
    width: 350px;
    margin: 0 auto;
}

:deep(#importMapTab .add-controls .title) {
    text-align: left;
    margin-top: 15px;
    margin-bottom: 15px;
    font-size: 18px;
    color: #757575;
}

:deep(#importMapTab .mapping-controls > div) {
    flex-grow: 5;
}

:deep(#importMapTab .mapping-controls .update-behaviour) {
    width: 100%;
}

:deep(#importMapTab .mapping-controls .update-behaviour .warning-label) {
    color: #ff0000;
    margin-left: 10px;
}

:deep([for="IMPORT_COMPLETED_tabList_importTool"]) {
    color: green;
}

:deep(#importMapTab .grid-wrapper) {
    height: 400px;
}

:deep([name^="DOWNLOAD_IMPORT_DETAILS"]) {
    text-transform: none;
}

:deep(.clearfix) {
    clear: both;
}
</style>
